// V2 slide-design Gallery — a categorised, live-preview showcase of every slide
// design in the system. Uses the Decks-style left sidebar for families and a
// click-to-open preview lightbox (the real slide, scaled large) before you use
// it. Each design is the REAL slide rendered + scaled (vanilla renderer +
// slide-stage), so it's a true preview, not a screenshot. Data-driven from
// SLIDE_LIBRARY + KIND_META, so it grows as per-layout starters land. Light DOM.

import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { navigate } from '../lib/router-state';
import { mountThumbs } from '../lib/thumb';
import { SLIDE_LIBRARY, type LibTemplate } from '../lib/slide-library';
import { KIND_META } from '../lib/kind-meta';
import { setPageTitle } from '../lib/page-title';
import type { Slide } from '../lib/slide-model';

const ALL = '__all__';

@customElement('gallery-page')
export class GalleryPage extends LitElement {
  @state() private active = ALL;
  @state() private previewId = '';
  @state() private previewLayout = '';

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    setPageTitle('Gallery', 'Every slide kind and layout, ready to drop into a deck.');
    document.addEventListener('keydown', this.onEsc);
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.onEsc);
  }
  private onEsc = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.previewId) { this.previewId = ''; e.stopPropagation(); }
  };

  private slideFor(id: string): Slide | undefined {
    // lib:<tid> or lib:<tid>:<layoutId> — the template, optionally in a layout.
    const rest = id.startsWith('lib:') ? id.slice(4) : id;
    const ci = rest.indexOf(':');
    const tid = ci === -1 ? rest : rest.slice(0, ci);
    const layout = ci === -1 ? '' : rest.slice(ci + 1);
    for (const g of SLIDE_LIBRARY) {
      const t = g.items.find((x) => x.id === tid);
      if (t) return layout ? ({ ...t.slide, layout } as Slide) : t.slide;
    }
    return undefined;
  }
  private templateFor(tid: string): LibTemplate | undefined {
    for (const g of SLIDE_LIBRARY) { const t = g.items.find((x) => x.id === tid); if (t) return t; }
    return undefined;
  }

  protected updated(): void {
    mountThumbs(this, (id) => this.slideFor(id));
  }

  /** Open a template in the Studio as the start of a new deck, optionally in a
   *  chosen layout. */
  private use(tid: string, layout = ''): void {
    try {
      sessionStorage.setItem('rdx-decks-template', tid);
      if (layout) sessionStorage.setItem('rdx-decks-template-layout', layout);
      else sessionStorage.removeItem('rdx-decks-template-layout');
    } catch { /* ignore */ }
    navigate('/studio');
  }

  /** Open the preview lightbox for a template, resetting the layout selection. */
  private openPreview(tid: string): void {
    this.previewLayout = '';
    this.previewId = tid;
  }
  /** The layout currently shown in the preview (selected, else the kind default). */
  private effLayout(kind: Slide['kind']): string {
    const ids = KIND_META[kind].layouts.map((l) => l.id);
    return this.previewLayout && ids.includes(this.previewLayout) ? this.previewLayout : ids[0];
  }

  private count(group: string): number {
    if (group === ALL) return SLIDE_LIBRARY.reduce((n, g) => n + g.items.length, 0);
    return SLIDE_LIBRARY.find((g) => g.group === group)?.items.length ?? 0;
  }
  private activeLabel(): string {
    return this.active === ALL ? 'All designs' : this.active;
  }

  render(): TemplateResult {
    const shown = this.active === ALL ? SLIDE_LIBRARY : SLIDE_LIBRARY.filter((g) => g.group === this.active);
    return html`
      <div class="library gallery-page">
        <aside class="lib-side">
          <div class="side-label">Families</div>
          <div class="side-group">
            <button class="side-item ${this.active === ALL ? 'is-active' : ''}" @click=${() => { this.active = ALL; }}>
              <span class="side-ico">▦</span><span class="side-name">All designs</span><span class="side-count">${this.count(ALL)}</span></button>
            ${SLIDE_LIBRARY.map((g) => html`
              <button class="side-item ${this.active === g.group ? 'is-active' : ''}" @click=${() => { this.active = g.group; }}>
                <span class="side-ico">◧</span><span class="side-name">${g.group}</span><span class="side-count">${this.count(g.group)}</span></button>`)}
          </div>
        </aside>

        <main class="lib-main">
          <div class="lib-bar">
            <div class="lib-bar-l">
              <h1 class="lib-h1">${this.activeLabel()}</h1>
              <span class="lib-count faint">${this.active === ALL ? this.count(ALL) : this.count(this.active)} designs · click to preview</span>
            </div>
          </div>
          ${shown.map((g) => html`
            <section class="gal-group">
              ${this.active === ALL ? html`<div class="gal-group-head"><h2 class="gal-group-title">${g.group}</h2><span class="faint">${g.items.length}</span></div>` : nothing}
              <div class="gal-grid">
                ${g.items.map((t) => {
                  const meta = KIND_META[t.slide.kind];
                  return html`
                    <div class="gal-card">
                      <button class="gal-preview-btn" aria-label=${`Preview ${t.name}`} @click=${() => this.openPreview(t.id)}>
                        <div class="gal-preview" data-thumb="lib:${t.id}"></div>
                      </button>
                      <div class="gal-meta">
                        <div class="gal-name">${t.name}</div>
                        <div class="gal-desc faint">${meta?.purpose || t.desc}</div>
                        <div class="gal-tags"><span class="gal-kind">${t.slide.kind}</span>${meta ? html`<span class="faint">· ${meta.layouts.length} layouts</span>` : nothing}</div>
                        <div class="gal-actions">
                          <button class="btn btn--ghost btn--sm" @click=${() => this.openPreview(t.id)}>Preview</button>
                          <button class="btn btn--ai btn--sm" @click=${() => this.use(t.id)}>Use template</button>
                        </div>
                      </div>
                    </div>`;
                })}
              </div>
            </section>`)}
        </main>
        ${this.previewId ? this.renderPreview() : nothing}
      </div>`;
  }

  private renderPreview(): TemplateResult {
    const t = this.templateFor(this.previewId);
    if (!t) return html``;
    const meta = KIND_META[t.slide.kind];
    const layouts = meta?.layouts ?? [];
    const eff = this.effLayout(t.slide.kind);
    return html`
      <div class="scrim" @mousedown=${(e: MouseEvent) => { if (e.target === e.currentTarget) this.previewId = ''; }}>
        <div class="gal-modal" role="dialog" aria-modal="true" aria-label=${`${t.name} preview`}>
          <div class="gal-modal-stage"><div class="gal-preview-lg" data-thumb=${`lib:${t.id}:${eff}`}></div></div>
          ${layouts.length > 1 ? html`
            <div class="gal-layouts" role="listbox" aria-label="Layouts">
              ${layouts.map((l) => html`
                <button class="gal-layout ${eff === l.id ? 'on' : ''}" role="option" aria-selected=${eff === l.id} title=${l.blurb || l.label} @click=${() => { this.previewLayout = l.id; }}>
                  <div class="gal-layout-thumb" data-thumb=${`lib:${t.id}:${l.id}`}></div>
                  <span class="gal-layout-name">${l.label}</span>
                </button>`)}
            </div>` : nothing}
          <div class="gal-modal-bar">
            <div class="gal-modal-info">
              <div class="gal-modal-name">${t.name}</div>
              <div class="gal-modal-desc faint">${meta?.purpose || t.desc}${meta ? ` · Good for: ${meta.goodForData}` : ''}</div>
            </div>
            <span class="gal-kind">${t.slide.kind}${layouts.length ? ` · ${layouts.length} layouts` : ''}</span>
            <span class="spacer" style="flex:1"></span>
            <button class="btn btn--ghost btn--sm" @click=${() => { this.previewId = ''; }}>Close</button>
            <button class="btn btn--ai gal-modal-use" @click=${() => this.use(t.id, eff)}>${eff === layouts[0]?.id ? 'Use this template' : `Use · ${layouts.find((l) => l.id === eff)?.label || eff}`}</button>
          </div>
        </div>
      </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'gallery-page': GalleryPage }
}
